// =============================================================================
// scenarioForCall — map a dial's funnel to the phone-setter course scenario
// =============================================================================
// The Course Scenario panel (CourseScenarioPanel) shows the exact script a phone
// setter SHOULD run for the lead being reviewed, beside the call transcript. This
// map turns the call's funnel-at-dial (the arrival event_type the connects RPC
// already returns as `source`) into the scenario markdown file to open.
//
// v1 routes by FUNNEL only. The connects payload does not yet carry the lead's
// triage_outcome_tag (Did Not Show / Showed - Did Not Book / Booked - Did Not
// Show), so we open the funnel's OUTBOUND doc — which itself holds all three
// sub-openers as sections — and let the reviewer scroll or switch. Phase 2 adds
// the outcome tag to get_dialer_connects and jumps to the exact sub-section.
//
// Files live in altara/public/course/phone-setter/ (copied from atlas
// resources/phone-setter-canvas2 by the course sync). Fetched at click time.
// Closers get the closer outbound course instead (CourseTrack, picked from the caller's role).
// =============================================================================

import { logger } from "../lib/logger";

export interface CourseScenario {
  /** File name under /course/phone-setter/ (no path, no leading slash). */
  file: string;
  /** Human label for the picker + panel header. */
  title: string;
}

/** Which course the review panel reads. Picked from the CALLER's role. */
export type CourseTrack = "phone-setter" | "closer";

/** Pure rule (unit-tested): only an exact "closer" role reads the closer
 *  course, and only when closer docs are bundled. Everything else (dialer,
 *  setter, exec, null, unknown, or no closer docs) keeps the phone-setter course. */
export function trackFor(role: string | null | undefined, closerDocsAvailable: boolean): CourseTrack {
  return role === "closer" && closerDocsAvailable ? "closer" : "phone-setter";
}

// Lazy raw-markdown loaders — one dynamic chunk per scenario, bundled by Vite
// from the module graph. NOT a public/ fetch, so there is no SPA-fallback trap
// (a missing public file returns index.html with a 200, which then renders as
// garbage — the 2026-09-17 bug this replaced). Vite resolves the glob at
// transform time by reading disk, so new scenario files are picked up on HMR.
const loaders = import.meta.glob("/content/course/phone-setter/*.md", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

// Closer course (content/course/closer-full/, written by
// scripts/sync_closer_course_full.py). File names carry a 3-digit TAB
// POSITION prefix that shifts when a tab is added upstream, and the sync
// never deletes old files, so closer docs are resolved by stem against the
// sync's index.json (current files only), never by number or folder listing.
const closerLoaders = import.meta.glob("/content/course/closer-full/*.md", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

// Key the loaders by bare file name (the SCENARIOS + funnel map use bare names).
// The two folders share no file names, so one map holds both.
const loaderByFile: Record<string, () => Promise<string>> = {};
for (const [path, loader] of [...Object.entries(loaders), ...Object.entries(closerLoaders)]) {
  const name = path.split("/").pop();
  if (name) loaderByFile[name] = loader;
}

/** True if a scenario file is bundled and loadable. */
export function hasScenario(file: string): boolean {
  return file in loaderByFile;
}

/** Load a scenario's markdown text (lazy dynamic import). Throws if unknown. */
export function loadScenario(file: string): Promise<string> {
  const loader = loaderByFile[file];
  if (!loader) return Promise.reject(new Error(`Unknown scenario: ${file}`));
  return loader();
}

/** The pure stem resolver (exported for the unit test). Returns the one file
 *  named `NNN_<stem>` (exactly 3 digits, then "_", then stem). Null when zero or
 *  more than one file matches, so a renamed or duplicated doc drops out of the
 *  list instead of loading the wrong text. */
export function resolveByStem(files: string[], stem: string): string | null {
  const hits = files.filter((f) => /^\d{3}_/.test(f) && f.slice(4) === stem);
  return hits.length === 1 ? hits[0] : null;
}

// Curated dialing scenarios (the lead types + the load-bearing masterclasses).
// Not every phase file — only what a reviewer checks a live dial against.
export const SCENARIOS: CourseScenario[] = [
  { file: "WHO_YOU_WILL_BE_DIALING.md", title: "Who You'll Be Dialing (map)" },
  // Outbound (dead ≥7 days, Lost Leads Page)
  { file: "OUTBOUND_THE_SYSTEM.md", title: "Outbound · The System" },
  { file: "OUTBOUND_THE_SYSTEM__OUTBOUND_WEBINAR_LEADS.md", title: "Outbound · Webinar Leads" },
  { file: "OUTBOUND_THE_SYSTEM__OUTBOUND_APPLICATIONS.md", title: "Outbound · Applications" },
  {
    file: "OUTBOUND_THE_SYSTEM__OUTBOUND_LOW_TICKET_BUYERS.md",
    title: "Outbound · Low Ticket Buyers",
  },
  {
    file: "OUTBOUND_THE_SYSTEM__OUTBOUND_OPT_INS_LEAD_MAGNET.md",
    title: "Outbound · Opt-Ins / Lead Magnet",
  },
  {
    file: "OUTBOUND_THE_SYSTEM__OUTBOUND_SHOWED_BUT_DIDN_T_BUY.md",
    title: "Outbound · Showed, Didn't Buy",
  },
  // Inbound (fresh ≤7 days)
  { file: "INBOUND_THE_SYSTEM.md", title: "Inbound · The System" },
  { file: "INBOUND_THE_SYSTEM__INBOUND_FRESH_OPT_INS.md", title: "Inbound · Fresh Opt-Ins" },
  {
    file: "INBOUND_THE_SYSTEM__INBOUND_FRESH_APPLICATIONS.md",
    title: "Inbound · Fresh Applications",
  },
  {
    file: "INBOUND_THE_SYSTEM__INBOUND_NEW_LOW_TICKET_BUYERS.md",
    title: "Inbound · New Low Ticket Buyers",
  },
  {
    file: "INBOUND_THE_SYSTEM__INBOUND_PRE_WEBINAR_SHOW_UP_DIALS.md",
    title: "Inbound · Pre-Webinar Show-Up",
  },
  {
    file: "INBOUND_THE_SYSTEM__INBOUND_POST_WEBINAR_CONFIRMS.md",
    title: "Inbound · Post-Webinar Confirms",
  },
  {
    file: "INBOUND_THE_SYSTEM__INBOUND_GOLDEN_TICKET_IMPLEMENTATION_CALLS.md",
    title: "Inbound · Golden Ticket / Implementation",
  },
  // Masterclasses a reviewer cross-checks against
  { file: "Phase_Two__THE_QUALIFICATION_MASTERCLASS.md", title: "Qualification Masterclass" },
  {
    file: "WHO_YOU_WILL_BE_DIALING__OBJECTION_HANDLING_MASTERY_THE_COMPLETE_GUIDE.md",
    title: "Objection Handling (complete)",
  },
  { file: "WHO_YOU_WILL_BE_DIALING__No_Pick_Up.md", title: "No Pick-Up Cadence" },
];

// The sync's own index (rewritten every run, current files only — orphans
// left on disk by a renumber are NOT in it). Same glob CoursePage.tsx uses.
const closerIndex =
  Object.values(
    import.meta.glob<Array<{ file: string }>>("/content/course/closer-full/index.json", {
      eager: true,
      import: "default",
    })
  )[0] ?? [];
const closerFiles = closerIndex
  .map((r) => r.file)
  .filter((f) => `/content/course/closer-full/${f}` in closerLoaders); // glob keys are full paths

// Closer outbound docs (the triage / Lost Leads work closers dial). ONE
// group so the picker is a flat list. Order = picker order. The Full
// Outbound Course is the auto-pick for every funnel.
const CLOSER_DEFAULT_STEM = "THE_FULL_OUTBOUND_COURSE.md";
const CLOSER_STEMS: Array<{ stem: string; title: string }> = [
  { stem: CLOSER_DEFAULT_STEM, title: "Outbound · The Full Course" },
  { stem: "Full_Outbound_Course_The_Calls.md", title: "Outbound · The Calls" },
  { stem: "Lost_leads_altara.md", title: "Outbound · Lost Leads Page" },
  { stem: "THE_BOOKED_LEAD_TRIAGE_CALL.md", title: "Outbound · Booked-Lead Triage Call" },
];
export const CLOSER_SCENARIOS: CourseScenario[] = CLOSER_STEMS.flatMap(({ stem, title }) => {
  const file = resolveByStem(closerFiles, stem);
  if (!file) {
    // A course re-sync renamed or duplicated this doc. Loud, not silent:
    // the doc drops out of the closer picker until CLOSER_STEMS is updated.
    logger.error(
      `scenarioForCall: closer doc stem "${stem}" resolved to 0 or 2+ files in closer-full/index.json`
    );
    return [];
  }
  return [{ file, title, group: "Outbound" as const }];
});
const CLOSER_DEFAULT_FILE =
  CLOSER_SCENARIOS.find((s) => s.file.slice(4) === CLOSER_DEFAULT_STEM)?.file ??
  CLOSER_SCENARIOS[0]?.file ??
  null;

/** The caller's role → course track (closer only when closer docs resolved). */
export function courseTrackForRole(role: string | null | undefined): CourseTrack {
  return trackFor(role, CLOSER_SCENARIOS.length > 0);
}

/** The picker list for a track. */
export function scenariosForTrack(track: CourseTrack): CourseScenario[] {
  return track === "closer" && CLOSER_SCENARIOS.length > 0 ? CLOSER_SCENARIOS : SCENARIOS;
}

/** Load a track's whole corpus as { file, text } (for the script-match
 *  highlights). Phone-setter = every phone-setter file (unchanged); closer =
 *  only the resolved closer docs. A failed file resolves to "" so one bad chunk
 *  never sinks the batch. */
export function loadAllScenarios(track: CourseTrack): Promise<Array<{ file: string; text: string }>> {
  const files =
    track === "closer"
      ? CLOSER_SCENARIOS.map((s) => s.file)
      : Object.keys(loaders).map((p) => p.split("/").pop() ?? "");
  return Promise.all(
    files.map((file) =>
      loadScenario(file)
        .then((text) => ({ file, text }))
        .catch(() => ({ file, text: "" }))
    )
  );
}

// The index/fallback scenario when the funnel is unknown or unlinked.
const DEFAULT_FILE = "WHO_YOU_WILL_BE_DIALING.md";

// funnel_at_dial (arrival event_type) → default scenario file. A dead webinar
// lead and a fresh one share the funnel, so v1 opens the OUTBOUND doc (it names
// every sub-case); the reviewer switches to the inbound variant via the picker.
const FUNNEL_TO_FILE: Record<string, string> = {
  webinar_signup: "OUTBOUND_THE_SYSTEM__OUTBOUND_WEBINAR_LEADS.md",
  dqwebinarsignup: "OUTBOUND_THE_SYSTEM__OUTBOUND_WEBINAR_LEADS.md",
  typeform_submission: "OUTBOUND_THE_SYSTEM__OUTBOUND_APPLICATIONS.md",
  call_booked: "INBOUND_THE_SYSTEM__INBOUND_POST_WEBINAR_CONFIRMS.md",
};

/** The scenario file to open for a call's funnel-at-dial. Falls back to the map. */
function scenarioFileForFunnel(funnelAtDial: string | null | undefined): string {
  if (!funnelAtDial) return DEFAULT_FILE;
  return FUNNEL_TO_FILE[funnelAtDial] ?? DEFAULT_FILE;
}

/** The auto-picked file for a call: closer track → The Full Outbound Course
 *  (or the first resolved closer doc); phone-setter track → the funnel map. */
export function scenarioFileFor(track: CourseTrack, funnelAtDial: string | null | undefined): string {
  if (track === "closer" && CLOSER_DEFAULT_FILE) return CLOSER_DEFAULT_FILE;
  return scenarioFileForFunnel(funnelAtDial);
}

/** Title for a scenario file (for the panel header); falls back to the file stem. */
export function titleForFile(file: string): string {
  return [...SCENARIOS, ...CLOSER_SCENARIOS].find((s) => s.file === file)?.title ?? file.replace(/\.md$/, "");
}
